import { useEffect, useState } from "react";
import { api } from "../../../lib/api";
import { UsersThree, CalendarCheck, Money, ClipboardText } from "@phosphor-icons/react";

const thisMonth = () => new Date().toISOString().slice(0, 7);
const inr = (v) => `₹${(v || 0).toLocaleString("en-IN")}`;

const Row = ({ label, value, strong }) => (
  <div className="flex justify-between text-sm py-1">
    <span className="text-stone-500">{label}</span>
    <span className={`tabular-nums ${strong ? "font-extrabold" : "font-semibold"}`}>{value}</span>
  </div>
);

const Card = ({ icon: Icon, title, children, testId }) => (
  <div className="bg-white border border-stone-200 rounded-lg p-5" data-testid={testId}>
    <div className="flex items-center gap-2 mb-3">
      <Icon size={20} weight="duotone" className="text-primary" />
      <h3 className="font-heading font-bold text-sm">{title}</h3>
    </div>
    {children}
  </div>
);

export const DigestPanel = () => {
  const [month, setMonth] = useState(thisMonth());
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/reports/digest", { params: { month } }).then((r) => setD(r.data)).catch(() => setD(null));
  }, [month]);

  return (
    <div className="space-y-4" data-testid="digest-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-extrabold">Monthly HR digest</h2>
          {d && <p className="text-xs text-stone-500 mt-0.5">{d.partial ? `Month in progress — figures as of ${d.as_of}` : `Full month summary for ${d.month}`}</p>}
        </div>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="digest-month-input"
          className="min-h-[44px] px-3 rounded-lg border border-stone-300 bg-white" />
      </div>

      {d && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card icon={UsersThree} title="Headcount" testId="digest-headcount">
            <Row label="Active employees" value={d.headcount.active} strong />
            <Row label="Joiners this month" value={d.headcount.joiners} />
            <Row label="Exits (FnF settled)" value={d.headcount.exits} />
          </Card>

          <Card icon={CalendarCheck} title="Attendance" testId="digest-attendance">
            <Row label="Attendance" value={`${d.attendance.attendance_pct}%`} strong />
            <Row label="Working days" value={d.attendance.working_days} />
            <Row label="Present (person-days)" value={d.attendance.present_days} />
            <Row label="Overtime hours" value={d.attendance.overtime_hours} />
            <Row label="Approved leave days" value={d.leave.approved_days} />
          </Card>

          <Card icon={Money} title={`Payroll — ${d.payroll.status}`} testId="digest-payroll">
            <Row label="Net payout" value={inr(d.payroll.total_net)} strong />
            <Row label="Gross" value={inr(d.payroll.total_gross)} />
            <Row label="Deductions" value={inr(d.payroll.total_deductions)} />
            <Row label="Reimbursements" value={inr(d.payroll.reimbursements)} />
            <Row label="Advance recovery" value={inr(d.payroll.loan_recovery)} />
            <Row label="LOP days" value={d.payroll.lop_days} />
          </Card>

          <Card icon={ClipboardText} title="Pending approvals" testId="digest-approvals">
            <Row label="Leave requests" value={d.approvals.pending_leaves} strong />
            <Row label="Regularizations" value={d.approvals.pending_regularizations} strong />
          </Card>
        </div>
      )}
    </div>
  );
};
